import { getCurrentApi } from './baseAPI';

class FundsAPI {
  // Get all users with pagination and search
  async getUsers(options = {}) {
    try {
      const api = await getCurrentApi();
      const params = new URLSearchParams();
      
      if (options.page) params.append('page', options.page);
      if (options.limit) params.append('limit', options.limit);
      if (options.search) params.append('search', options.search);

      const queryString = params.toString();
      const url = queryString ? `/funds/users?${queryString}` : '/funds/users';
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch users:', error);
      throw error;
    }
  }

  // Add funds to user
  async addFunds(userId, amount, description = '', adminInfo = {}, metadata = {}) {
    try {
      const api = await getCurrentApi();
      const data = {
        userId,
        amount: parseFloat(amount),
        description,
        adminInfo: {
          adminId: adminInfo.adminId || 'system',
          adminName: adminInfo.adminName || 'System Admin',
          ...adminInfo
        },
        metadata
      };

      const response = await api.post('/funds/add', data);
      return response.data;
    } catch (error) {
      console.error('Failed to add funds:', error);
      throw error;
    }
  }

  // Remove funds from user
  async removeFunds(userId, amount, description = '', adminInfo = {}) {
    try {
      const api = await getCurrentApi();
      const data = {
        userId,
        amount: parseFloat(amount),
        description,
        adminInfo: {
          adminId: adminInfo.adminId || 'system',
          adminName: adminInfo.adminName || 'System Admin',
          ...adminInfo
        }
      };

      const response = await api.post('/funds/remove', data);
      return response.data;
    } catch (error) {
      console.error('Failed to remove funds:', error);
      throw error;
    }
  }

  // Adjust user balance
  async adjustBalance(userId, amount, description = '', adminInfo = {}) {
    try {
      const api = await getCurrentApi();
      const data = {
        userId,
        amount: parseFloat(amount),
        description,
        adminInfo: {
          adminId: adminInfo.adminId || 'system',
          adminName: adminInfo.adminName || 'System Admin',
          ...adminInfo
        }
      };

      const response = await api.post('/funds/adjust', data);
      return response.data;
    } catch (error) {
      console.error('Failed to adjust balance:', error);
      throw error;
    }
  }

  // Get user balance
  async getUserBalance(userId) {
    try {
      const api = await getCurrentApi();
      const response = await api.get(`/funds/user/${userId}/balance`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user balance:', error);
      throw error;
    }
  }

  // Get user transactions with pagination and filtering
  async getUserTransactions(userId, options = {}) {
    try {
      const api = await getCurrentApi();
      const params = new URLSearchParams();
      
      if (options.page) params.append('page', options.page);
      if (options.limit) params.append('limit', options.limit);
      if (options.type) params.append('type', options.type);
      if (options.status) params.append('status', options.status);
      if (options.startDate) params.append('startDate', options.startDate);
      if (options.endDate) params.append('endDate', options.endDate);

      const queryString = params.toString();
      const url = queryString ?
        `/funds/user/${userId}/transactions?${queryString}` :
        `/funds/user/${userId}/transactions`;
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user transactions:', error);
      throw error;
    }
  }

  // Get user transaction summary
  async getUserTransactionSummary(userId) {
    try {
      const api = await getCurrentApi();
      const response = await api.get(`/funds/user/${userId}/summary`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user transaction summary:', error);
      throw error;
    }
  }

  // Get all transactions (admin view)
  async getAllTransactions(options = {}) {
    try {
      const api = await getCurrentApi();
      const params = new URLSearchParams();
      
      if (options.page) params.append('page', options.page);
      if (options.limit) params.append('limit', options.limit);
      if (options.userId) params.append('userId', options.userId);
      if (options.type) params.append('type', options.type);
      if (options.status) params.append('status', options.status);
      if (options.startDate) params.append('startDate', options.startDate);
      if (options.endDate) params.append('endDate', options.endDate);

      const queryString = params.toString();
      const url = queryString ? `/funds/transactions?${queryString}` : '/funds/transactions';
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch all transactions:', error);
      throw error;
    }
  }

  // Get transaction by ID
  async getTransactionById(transactionId) {
    try {
      const api = await getCurrentApi();
      const response = await api.get(`/funds/transactions/${transactionId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch transaction by ID:', error);
      throw error;
    }
  }

  // Get user statement for a given period
  async getUserStatement(userId, startDate, endDate, options = {}) {
    try {
      const api = await getCurrentApi();
      const params = new URLSearchParams();

      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (options.page) params.append('page', options.page);
      if (options.limit) params.append('limit', options.limit);

      const queryString = params.toString();
      const url = queryString
        ? `/funds/user/${userId}/statement?${queryString}`
        : `/funds/user/${userId}/statement`;

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user statement:', error);
      throw error;
    }
  }

  // Export user statement as PDF or XLSX file (returns download URL Blob)
  async exportStatement(userId, startDate, endDate, format = 'pdf') {
    try {
      const api = await getCurrentApi();
      const params = new URLSearchParams();

      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      params.append('format', format);

      const url = `/funds/user/${userId}/statement/export?${params.toString()}`;

      const response = await api.get(url, { responseType: 'blob' });
      return response;
    } catch (error) {
      console.error('Failed to export statement:', error);
      throw error;
    }
  }

  // ─── Base Currency Operations ─────────────────────────────────────────

  // Change user's base currency
  async changeBaseCurrency(userId, targetCurrency, spreadPercent) {
    try {
      const api = await getCurrentApi();
      const response = await api.post(`/users/${userId}/change-base-currency`, {
        targetCurrency,
        spreadPercent: parseFloat(spreadPercent)
      });
      return response.data;
    } catch (error) {
      console.error('Failed to change base currency:', error);
      throw error;
    }
  }

  // Validate if currency change is possible (dry-run)
  async validateCurrencyChange(userId, targetCurrency, spreadPercent) {
    try {
      const api = await getCurrentApi();
      const response = await api.post(`/users/${userId}/validate-currency-change`, {
        targetCurrency,
        spreadPercent: spreadPercent !== undefined ? parseFloat(spreadPercent) : undefined
      });
      return response.data;
    } catch (error) {
      console.error('Failed to validate currency change:', error);
      throw error;
    }
  }

  // Get list of available currencies for base currency
  async getAvailableCurrencies() {
    try {
      const api = await getCurrentApi();
      const response = await api.get('/users/available-currencies');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch available currencies:', error);
      throw error;
    }
  }
}

export const fundsAPI = new FundsAPI();
export default fundsAPI;